import {
  Box,
  Center, Divider, Flex, Heading, Text, useColorModeValue
} from "@chakra-ui/react";
import { useSession } from "next-auth/react";
import { useRouter } from "next/dist/client/router";
import * as React from "react";
import {
  guessTimezone
} from "../../controllers/settings";
import { useFirebaseUser } from "../../utils/useFirebaseUser";
import { PlansLeaders } from "../pricing/plansLeaders";
import { IFrameTestimonials } from "./IFrameTestimonials";

const BillingFrequencyTypes = ["monthly", "yearly"] as const;
export type BillingFrequency = typeof BillingFrequencyTypes[number];

function MeetOurUsers() {
  const color = useColorModeValue("taplio.500", "blue.300");

  return (
    <Box
      as="section"
      mb="50px"
      display="flex"
      flexDirection="column"
      alignItems="center"
      maxW={{ base: "xl", md: "6xl" }}
      mx="auto"
      w="full"
    >
      <Center mb={16}>
        <Heading
          mt="4"
          px={[0, 4]}
          textAlign="center"
          fontWeight="900" // fontSize="46px"
          maxW="500px"
          {...{
            fontFamily: "inter",
            color,
            fontSize: "2xl",
          }}
        >
          <b>Meet some of our very happy users</b>
        </Heading>
      </Center>
      <IFrameTestimonials />
    </Box>
  );
}

function FAQ() {
  return (
    <Box as="section" py="10" fontFamily={"inter"} px={8}>
      <Box
        maxW={{
          base: "xl",
          md: "4xl",
        }}
        mx="auto"
      // px={{
      //   base: "6",
      //   md: "8",
      // }}
      >
        <Center mb={24}>
          <Heading
            px={4}
            mt="4"
            textAlign="center"
            fontWeight="900" // fontSize="46px"
            maxW="500px"
            {...{
              fontFamily: "inter",
              color: "taplio.500",
              fontSize: "2xl",
            }}
          >
            <b>Still have questions?</b>
          </Heading>
        </Center>
        {[
          {
            q: "How is Taplio different from any other LinkedIn tool?",
            a: "Contrary to other LinkedIn tools, we don’t focus on automation and lead generation. What we focus on is helping you build a personal brand as well as meaningful relationships on LinkedIn. That translates into features that are here to help you create more content, interact with more people, and overall be focused on attracting opportunities organically more than through automated DM sequences.",
          },
          {
            q: "Which plan is right for me?",
            a: "If your main struggle is writing and scheduling posts, we believe the Starter plan is sufficient for you. However, the Starter plan doesn’t provide access to AI-powered features or Engagement features. If this is what you’re looking for, we recommend you pick the Standard plan or the Pro plan, depending on how intensively you plan on using these features. The Standard plan is ideal for beginners looking to improve on LinkedIn and get their personal brand started on the right foot, while the Pro plan is very much adapted to larger accounts or ones looking to boost their growth rapidly. It’s up to you!",
          },
          {
            q: "How does the 30-day money-back guarantee work?",
            a: "It’s very simple. If, for whatever reason, you want to get your money back within 30 days of your purchase, we will refund your payment. We won’t try to keep you or ask you to justify yourself. Whether you forget to cancel, can’t afford Taplio anymore or simply don’t want to keep using it, just reach out to our support team through the app and we’ll be on the other side doing the refund.",
          },
          {
            q: "When will I get charged?",
            a: "You will get charged at the end of your 7-day free trial. But as mentioned, you can get a refund for up to 30 days, no questions asked.",
          },
          {
            q: "Why do you ask for card details to start a trial?",
            a: (
              <>
                There’s 2 main reasons we ask for card details: 1) our AI technology is expensive to run, and 2) we are still a small team trying to focus on what our paying customers need. By asking for card details, this ensures we only have people who are at the very least motivated to build their personal brand and not “just curious” about the tool.
                <br />
                <br />
                But we don’t want people to get charged just because they forgot to cancel their trial. It doesn’t make economical sense for us either and it doesn’t benefit users. Which is exactly why we have a 30-day refund policy.
              </>
            ),
          },
          {
            q: "Is Taplio safe to use?",
            a: "Because we focus on content inspiration, engaging and analytics, we are safer to use than most tools out there that involve heavy automation. We make it a priority to keep our users’ accounts safe, which is also why we can sometimes warn you or limit some of the actions you can take in Taplio because we believe they might be a bit risky. Generally speaking, there is always a risk involved when using a tool for LinkedIn, but we mitigate that risk as well as we can.",
          },
          {
            q: "Can you tell me more about how AI works in Taplio?",
            a: (
              <>
                Gladly! Taplio uses the most advanced technologies available paired with our expertise at training them for the purpose of building a brand on LinkedIn. Those technologies include GPT-4, the absolute best AI model available today, as well as GPT-3.5, it’s excellent though slightly inferior counterpart.
                <br />
                <br />
                Because of its cost, GPT-4 usage is linked to a credit system (or quota) which is reset on the 1st of every month. Whenever you use AI in Taplio, whether with Chat Assist or one of our post or comment generators, you consume 1 credit. Whenever you run out of credits, your account is switched over to GPT-3.5 which is also very good at what it does, quicker, but slightly less high quality. When subscribing to the Standard plan, you get 500 GPT-4 credits per month, when subscribing to the Pro plan gets you a whopping 5,000 credits.
              </>
            ),
          },
          {
            q: "How do you provide customer support?",
            a: "Support is provided straight within a dedicated support module in the Taplio application. We try to respond to most inquiries a quickly as possible but, if it turns out we can’t, then the rest of the conversation will happen through email.",
          },
          {
            q: "Can I cancel anytime?",
            a: "Yes. And you can cancel straight in the application’s Settings. It’s quite straightforward. When cancelling, you may also request a refund of your latest payment as long as it has occured in the last 30 days.",
          },
        ].map(({ q, a }, mapIndex) => (
          <React.Fragment key={(mapIndex + 1) + "_key"}>
            <Flex
              {...{
                flexDirection: "column",
              }}
            >
              <Text
                {...{
                  fontFamily: "inter",
                  color: "taplio.500",
                  fontSize: "lg",
                  fontWeight: 700,
                  mb: 8,
                }}
              >
                {q}
              </Text>
              <Text
                {...{
                  fontFamily: "inter",
                  fontSize: "lg",
                  fontWeight: 400,
                  mb: 12,
                }}
              >
                {a}
              </Text>
            </Flex>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

export const OnboardNew = ({ discount, withCounter = false }) => {
  const router = useRouter();
  const { data: session, status } = useSession() ?? {}
  const loading = status === "loading"
  const isAuthenticated = useFirebaseUser();
  const [billingFrequency, setBillingFrequency] =
    React.useState<BillingFrequency>("monthly");

  React.useEffect(() => {
    if (isAuthenticated) guessTimezone(session);
  }, [session, isAuthenticated]);

  return (
    <>
      <Flex flexDirection={"column"}>
        <Flex as="section" py="10" flexDirection={"column"}>
          <Box
            maxW={{
              base: "xl",
              md: "4xl",
            }}
            mx="auto"
            px={{ base: "6", md: "8" }}
          >
            <PlansLeaders
              billingFrequency={billingFrequency}
              discount={discount}
              customCancelUrl="pricing"
              withCounter={withCounter}
            />
          </Box>
        </Flex>

        <Divider
          width="40"
          borderBottomWidth="2px"
          mb="50px"
          // mt="50px"
          placeSelf={"center"}
        />

        {/* <Box
          as="section"
          mb="50px"
          display="flex"
          flexDirection="column"
          alignItems="center"
          maxW={{ base: "xl", md: "6xl" }}
          mx="auto"
        > */}
        <MeetOurUsers></MeetOurUsers>
        {/* </Box> */}

        <Divider
          width="40"
          borderBottomWidth="2px"
          mb="50px"
          mt="50px"
          placeSelf={"center"}
        />

        <FAQ></FAQ>
      </Flex>
    </>
  );

  // return (
  //   // <Box as="section" bg={mode("gray.50", "gray.800")} py="10">
  //   <Box as="section" py="10">
  //     <Box
  //       maxW={{ base: "xl", md: "7xl" }}
  //       mx="auto"
  //       px={{ base: "6", md: "8" }}
  //     >
  //       <Box mt={20}>
  //         <PlansLeaders
  //           billingFrequency={billingFrequency}
  //           discount={""}
  //           customCancelUrl="pricing"
  //         />
  //       </Box>
  //     </Box>
  //     <Box
  //       mt={10}
  //       p={4}
  //       display="flex"
  //       flexDirection="column"
  //       alignItems="center"
  //     >
  //       <Heading textAlign="center" mt={8} mb={16}>
  //         See what people say about us
  //       </Heading>
  //       <Box minWidth="80%" maxWidth="1200px">
  //         <script
  //           type="text/javascript"
  //           src="https://testimonial.to/js/iframeResizer.min.js"
  //           dangerouslySetInnerHTML={{ __html: `` }}
  //         />
  //         <Iframe
  //           id="testimonialto-taplio-light"
  //           url={"https://embed.testimonial.to/w/taplio?theme=light&card=base"}
  //           width="100%"
  //           height="1400px"
  //         />
  //         <script
  //           type="text/javascript"
  //           dangerouslySetInnerHTML={{
  //             __html: `iFrameResize({log: false, checkOrigin: false}, "#testimonialto-taplio-light");`,
  //           }}
  //         />

  //         {/* <script type="text/javascript" src="https://testimonial.to/js/iframeResizer.min.js" dangerouslySetInnerHTML={{ __html: ``}}/>
  //         {
  //           typeof window !== 'undefined' && (
  //             <Iframe id="testimonialto-carousel-all-taplio-light" url={"https://embed.testimonial.to/carousel/all/taplio?theme=light&autoplay=off&showmore=off&one-row=on&same-height=on"} width="100%" height="600px"/>
  //           )
  //         }
  //         <script type="text/javascript" dangerouslySetInnerHTML={{ __html: `iFrameResize({log: false, checkOrigin: false}, "#testimonialto-carousel-all-taplio-light");`}}/> */}
  //       </Box>
  //     </Box>
  //     <Box>
  //       {/* <Flex direction="column" justify="center" alignItems="center">
  //           <Payment
  //             // color="blue.400"
  //             textColor={mode("white","gray.900")}
  //             plan={billingFrequency === "yearly" ? "growYearly" : "growMonthly"}
  //             billingFrequency={billingFrequency}
  //             session={session}
  //             customCancelUrl="onboardnew"
  //             source="onboardNew"
  //             onClickStartFreeTrial={() => {
  //               console.log("click start trial");
  //               if (session?.user?.data && !session.user.data.description) {
  //                 console.log("call warm");
  //                 warm(session);
  //               }
  //             }}
  //           />
  //         </Flex> */}
  //       <PlansLeaders
  //         billingFrequency={billingFrequency}
  //         discount={""}
  //         customCancelUrl="pricing"
  //       />
  //     </Box>
  //       </Box>

  // );
};
